/**
 * Whether one account (trustee's own or a sub-account, both represented as
 * {signedByClass, ownershipByClass}) has voted for every campaign class it owns.
 */
function isAccountDone(campaignClasses, acc) {
	return campaignClasses.every((cls) => acc.signedByClass[cls] || acc.ownershipByClass[cls] == 0);
}

/**
 * Whether a trustee's controlled sub-accounts are all done voting -- signed for every
 * campaign class they own shares in, or own none of it -- used to gate the overview page's
 * "Vote Now" CTA (index.js's showCTAPanel()/mightBeAllDone()).
 *
 * matchedCount/requestedCount distinguish "every controlled sub-account was confirmed done"
 * from "the alias-names lookup came back partial or empty" (a failed/incomplete API response,
 * or a caller passing 0/0 to represent a rejected request). A workspace legitimately dropped
 * from `accounts` for owning zero shares still counts as matched -- only a workspace the API
 * response never accounted for at all should read as unconfirmed. Without this guard, an
 * incomplete lookup silently leaves the CTA at whatever the trustee's own account alone
 * already decided, which reads as "done" as soon as they sign their own ballot even though
 * sub-accounts they control are still pending.
 *
 * @param {string[]} campaignClasses
 * @param {Array<{signedByClass: Object, ownershipByClass: Object}>} accounts - matched sub-accounts
 * @param {number} matchedCount - workspaces the lookup actually accounted for (found or not)
 * @param {number} requestedCount - workspaces requested from the lookup
 * @returns {boolean}
 */
export function isTrusteeVotingComplete(campaignClasses, accounts, matchedCount, requestedCount) {
	if (matchedCount < requestedCount) return false;
	return accounts.every((acc) => isAccountDone(campaignClasses, acc));
}

/**
 * Count how many accounts (the trustee's own, plus controlled sub-accounts that own shares)
 * still need to vote and how many total there are, for a lightweight "N of M accounts voted"
 * status line under the overview page's Vote Now CTA.
 *
 * Same matchedCount/requestedCount guard as isTrusteeVotingComplete -- an incomplete lookup
 * returns null rather than a wrong or partial count, since the true total isn't known until
 * every requested workspace has been accounted for.
 *
 * @param {string[]} campaignClasses
 * @param {Array<{signedByClass: Object, ownershipByClass: Object}>} accounts - matched sub-accounts owning shares
 * @param {number} matchedCount
 * @param {number} requestedCount
 * @param {({signedByClass: Object, ownershipByClass: Object}|null|undefined)} ownAccount - the
 *   trustee's own account record, or falsy if they own no shares in this campaign themselves
 * @returns {({total: number, signed: number}|null)}
 */
export function countTrusteeVotingProgress(campaignClasses, accounts, matchedCount, requestedCount, ownAccount) {
	if (matchedCount < requestedCount) return null;
	const total = (ownAccount ? 1 : 0) + accounts.length;
	const signed = (ownAccount && isAccountDone(campaignClasses, ownAccount) ? 1 : 0) + accounts.filter((acc) => isAccountDone(campaignClasses, acc)).length;
	return { total, signed };
}
